if (!customElements.get('lazy-section-template')) {
  const COMPONENT_VERSION = '2026-03-11-v14';

  class LazySectionTemplate extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('template');
      if (!this.template) return;

      this.preloadOffset = Number(this.dataset.preloadOffset || 200);
      this.requireScroll = this.dataset.requireScroll === '1';
      this.hasUserScrolled = window.scrollY > 0;
      this.debug = this.dataset.debug === '1' || window.location.search.includes('lazyDebug=1');
      this.hydrated = false;
      this.observer = null;
      this.pendingHydrateOnScroll = false;

      this.onIntersect = this.onIntersect.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.log('init', {
        version: COMPONENT_VERSION,
        preloadOffset: this.preloadOffset,
        requireScroll: this.requireScroll,
        viewportHeight: window.innerHeight || 0,
        rect: this.getBoundingClientRect()
      });

      if (window.Shopify && Shopify.designMode) {
        this.hydrate('design_mode');
        return;
      }

      if (this.dataset.tabLazy === '1') {
        this.log('tab_lazy_mode — waiting for external hydrate call');
        return;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          rootMargin: `${this.preloadOffset}px 0px ${this.preloadOffset}px 0px`,
          threshold: 0
        });
        this.observer.observe(this);
      } else {
        this.hydrate('observer_unavailable');
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    onScroll() {
      this.hasUserScrolled = true;
      if (!this.pendingHydrateOnScroll || this.hydrated) return;
      this.pendingHydrateOnScroll = false;
      this.hydrate('observer_intersect_on_first_scroll_event');
    }

    onIntersect(entries) {
      this.log('intersect_entries_count', entries.length);
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];

        this.log('intersect', {
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio,
          targetIsSelf: entry.target === this,
          boundingClientRect: entry.boundingClientRect,
          rootBounds: entry.rootBounds
        });

        if (!entry.isIntersecting) continue;
        if (this.requireScroll && !this.hasUserScrolled && window.scrollY <= 0) {
          this.log('intersect_ignored_before_first_scroll');
          continue;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const { top, bottom } = entry.boundingClientRect;
        const isVisibleInViewport = top <= viewportHeight && bottom >= 0;

        if (isVisibleInViewport) {
          this.hydrate('observer_intersect_visible_in_viewport');
          break;
        }

        if (window.scrollY <= 0) {
          this.pendingHydrateOnScroll = true;
          this.log('intersect_deferred_until_scroll');
          continue;
        }

        this.hydrate('observer_intersect');
        break;
      }
    }

    hydrate(reason = 'hydrate') {
      if (this.hydrated || !this.template || !this.template.parentNode) return;
      this.hydrated = true;
      this.log('hydrate', reason, { rect: this.getBoundingClientRect() });

      this.insertBefore(this.template.content.cloneNode(true), this.template);
      this.template.remove();
      this.style.minHeight = '';

      this.cleanup();
      this.dispatchEvent(new CustomEvent('lazy-section-hydrated', { bubbles: true }));
    }

    log(...args) {
      if (!this.debug) return;
      const id = this.dataset.sectionId || this.id || 'unknown';
      console.debug('[lazy-section-template]', id, ...args);
    }

    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  customElements.define('lazy-section-template', LazySectionTemplate);
}